import { InputAdornment, ListItemIcon, MenuItem, TextField, Tooltip } from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
  DatePicker,
  DateTimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import classNames from "classnames";
import { Set } from "immutable";
import Moment from "moment";
import "moment/locale/pt-br";
import React from "react";

// Garante locale pt-br pro próprio módulo (algumas paths de import async
// instanciam o DatePicker antes do AppRoot setar o locale global).
Moment.locale("pt-br");

// eslint-disable-next-line camelcase
import { ATROCITY_some, mask } from "../../core/util";
import Autocomplete from "../Autocomplete2";
import ControlGroup from "../ControlGroup";
import FileChooser from "../FileChooser";
import FileList from "../FileList";
import ToggleSwitch from "../ToggleSwitch";
import { isControlGroupType, isDateType, isTextFieldType } from "./predicates";
import { setInputProps, setShrinkLabel } from "./props";

const MAIN_OPTION_VALUE = "@!MAIN_OPTION_VALUE";

/**
 * Mantido por compatibilidade com chamadas externas — antigamente o ícone
 * de info vinha embutido no label do TextField, mas isso fazia o ícone
 * "flutuar" sobre overlays (calendar do DatePicker, popovers, dropdowns)
 * porque o FloatingLabel do TextField fica em z-order acima do popper
 * quando o popper abre pra cima.
 *
 * Solução: mover o ícone pra `endAdornment` do TextField (faz parte do
 * input field, é coberto pelo popper). Agora `renderIconInfo` retorna
 * só o label (sem wrapping), e o adornment é injetado via `withHelpAdornment`
 * abaixo.
 */
function renderIconInfo(props) {
  return props.label;
}

/**
 * Cria o `<InputAdornment>` com o ícone (i) + tooltip pra ser injetado
 * no `endAdornment` do TextField/Date.
 */
function renderHelpAdornment(props) {
  const { message, onClickMessage } = props;
  if (!message) return null;
  return (
    <InputAdornment position="end">
      <Tooltip title={message} arrow placement="top">
        <InfoOutlined
          onClick={onClickMessage}
          style={{
            width: 18,
            height: 18,
            color: "rgba(96, 125, 139, 0.85)",
            cursor: onClickMessage ? "pointer" : "help",
          }}
        />
      </Tooltip>
    </InputAdornment>
  );
}

/**
 * Mescla um help adornment ao InputProps existente — preserva endAdornment
 * já configurado (counter, percentage, password, etc) e adiciona o ícone
 * de info ao lado.
 */
function withHelpAdornment(InputProps, props) {
  const help = renderHelpAdornment(props);
  if (!help) return InputProps;
  const existing = InputProps && InputProps.endAdornment;
  return {
    ...InputProps,
    endAdornment: existing ? (
      <>
        {existing}
        {help}
      </>
    ) : (
      help
    ),
  };
}

function renderMainOption(mainOption, classes) {
  if (!mainOption) {
    return null;
  }

  const { icon: Icon, label, onClick } = mainOption;
  return (
    <MenuItem onClick={onClick} value={MAIN_OPTION_VALUE} className={classes.mainAction}>
      {Icon && (
        <ListItemIcon className={classes.mainActionIcon}>
          <Icon />
        </ListItemIcon>
      )}
      {label}
    </MenuItem>
  );
}

/**
 * GAMBIARRA!!!
 *
 * ISSO É UMA GAMBIARRA PORQUE ALGUÉM TEVE A IDEIA BRILHANTE!!!!!!!!!!
 * DE USAR O COMPONENTE Form PARA FAZER FILTROS!!!!!!!!!!!!!!!!!!!!!!!
 * VIVA A CRIATIVIDADE!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
 */
let kkkk = 0;

function renderSelect(props, classes) {
  const {
    label,
    options,
    onChange,
    message,
    className,
    mainOption,
    style,
    minWidth = "150px",
    ...other
  } = props;

  const augmentedStyle = { ...style, minWidth };

  // Mergeia o slotProps que possa ter vindo de cima (input/inputLabel) com o
  // slotProps.select local (MenuProps). Antes era hardcoded, sobrescrevendo.
  const incomingSlotProps = other.slotProps || {};
  const otherWithoutSlotProps = { ...other };
  delete otherWithoutSlotProps.slotProps;
  // Injeta o ícone (i) de ajuda no endAdornment do field (em vez do label),
  // pra não flutuar sobre dropdowns/overlays. Veja withHelpAdornment.
  const otherInputProps = withHelpAdornment(otherWithoutSlotProps.InputProps, props);

  const mergedSlotProps = {
    ...incomingSlotProps,
    select: {
      MenuProps: { className: classes.select },
      ...(incomingSlotProps.select || {}),
    },
    // MUI v9 removeu `InputProps` do TextField — agora vai via slotProps.input.
    // Sem isso o prop vazava pro <div> root e o React reclamava
    // "React does not recognize the `InputProps` prop on a DOM element".
    input: {
      ...(incomingSlotProps.input || {}),
      ...otherInputProps,
    },
  };

  // Sobra `InputProps` em `otherWithoutSlotProps` por causa do legado — remove
  // pra evitar que o spread abaixo o reintroduza no TextField.
  const { InputProps: _droppedInputProps, ...textFieldRest } = otherWithoutSlotProps;

  return (
    <TextField
      select
      className={classNames(className, classes.select)}
      slotProps={mergedSlotProps}
      onChange={(x, y) => {
        const { value } = x.target;

        if (value === MAIN_OPTION_VALUE && mainOption) {
          if (mainOption.onClick) {
            mainOption.onClick();
          }
          if (mainOption.selectable) {
            onChange(x, y);
          }
        } else {
          onChange(x, y);
        }
      }}
      {...textFieldRest}
      style={augmentedStyle}
      label={label}
    >
      {renderMainOption(mainOption, classes)}
      {options &&
        options.map((opt) => {
          kkkk += 1;
          return (
            <MenuItem key={`${opt.get("value")}-${kkkk}`} value={opt.get("value")}>
              {opt.get("label")}
            </MenuItem>
          );
        })}
    </TextField>
  );
}

function renderControlGroup(props) {
  const { classes, ...others } = props;
  return (
    <div className={classes.marginControl}>
      <ControlGroup {...others} />
    </div>
  );
}

function getDateViews(views, isMonth, maxDate, minDate) {
  if (views || !isMonth) {
    return views;
  }
  if (maxDate && minDate) {
    const isThisYear = Moment.duration(minDate.diff(maxDate)).years() === 0;
    if (isThisYear) {
      return ["month"];
    }
  }
  return ["year", "month"];
}

function renderDateFieldInput(props) {
  const {
    type,
    value,
    views,
    label,
    error,
    openTo,
    onBlur,
    minDate,
    classes,
    maxDate,
    onFocus,
    variant,
    message,
    onChange,
    disabled,
    required,
    InputProps,
    noKeyboard,
    helperText,
    disablePast,
    placeholder,
    customFormat,
    disableFuture,
    InputLabelProps,
    customMask = null,
  } = props;

  const dateFormat = {
    date: "DD/MM/YYYY",
    month: "MM/YYYY",
    datetime: "DD/MM/YYYY [às] HH:mm",
  };

  const DateComponent = type === "datetime" ? DateTimePicker : DatePicker;
  const currentFormat = customFormat || dateFormat[type];
  // @mui/x-date-pickers v9 com AdapterMoment exige `value`, `minDate` e `maxDate`
  // como objetos `moment`. Form/state armazena ISO string — convertemos aqui.
  // Sem isso crasha com "value.isValid is not a function".
  const toMoment = (v) => {
    if (!v) return null;
    if (Moment.isMoment(v)) return v;
    return Moment(v);
  };
  const newValue = toMoment(value);
  const newMinDate = toMoment(minDate);
  const newMaxDate = toMoment(maxDate);
  const customViews = getDateViews(views, type === "month", newMinDate, newMaxDate);

  return (
    <div className={classes.marginDateField}>
      {/* LocalizationProvider local com pt-br fixo + instância de Moment
          pré-configurada (`dateLibInstance`). Esta é a forma DOCUMENTADA do
          MUI X v9 pra forçar locale: passar a instância já com locale
          ativo evita qualquer race condition entre import e render. */}
      <LocalizationProvider
        dateAdapter={AdapterMoment}
        adapterLocale="pt-br"
        dateLibInstance={Moment}
      >
        <DateComponent
          openTo={type === "month" ? customViews[0] : openTo}
          views={customViews}
          value={newValue}
          onChange={(newDate) => {
            onChange(newDate);
            if (onBlur) {
              onBlur(newDate);
            }
          }}
          label={message ? renderIconInfo(props) : label}
          format={currentFormat}
          disableFuture={disableFuture}
          disablePast={disablePast}
          minDate={newMinDate || undefined}
          maxDate={newMaxDate || undefined}
          disabled={disabled}
          slotProps={{
            textField: {
              fullWidth: true,
              variant,
              error,
              required,
              helperText,
              placeholder: placeholder || Moment().format(currentFormat),
              onFocus,
              onBlur,
              // Injeta o ícone (i) de ajuda como endAdornment do TextField
              // do DatePicker — assim ele NÃO fica no label flutuante (que
              // sobrepunha o calendar popper). Veja withHelpAdornment.
              InputProps: withHelpAdornment(InputProps, props),
              InputLabelProps,
            },
          }}
        />
      </LocalizationProvider>
    </div>
  );
}

function renderTextFieldInput(props) {
  const {
    min,
    max,
    type,
    label,
    value,
    noMask,
    shrink,
    classes,
    message,
    onChange,
    valueSize,
    inputProps,
    mainOption,
    InputProps,
    isPassword,
    InputLabelProps,
    SelectProps,
    readOnly = false,
    margin = "normal",
    isPasswordVisible,
    onVisibilityChange,
    ...other
  } = props;
  const { variant } = props;

  const newInputLabelProps = setShrinkLabel(InputLabelProps, shrink, type, value, variant);
  const newInputProps = setInputProps(
    InputProps,
    type,
    readOnly,
    onVisibilityChange,
    isPasswordVisible,
    isPassword,
    value,
    onChange,
    min,
    max,
  );

  // MUI v9 removeu `InputProps`, `InputLabelProps` e `SelectProps` do TextField.
  // Convertemos pra `slotProps.{input, inputLabel, select}` aqui no único ponto
  // onde são montados — evita warnings em massa "React does not recognize prop X".
  let newProps = {
    type,
    margin,
    onChange,
    slotProps: {
      input: newInputProps,
      inputLabel: newInputLabelProps,
      ...(SelectProps ? { select: SelectProps } : {}),
    },
    value: noMask ? value || "" : mask(type, value),
  };

  if (type === "counter") {
    // MUI v9: `inputProps` saiu — agora vai via `slotProps.htmlInput`.
    const counterProps = {
      slotProps: {
        ...newProps.slotProps,
        htmlInput: { style: { textAlign: "center" }, readOnly: true },
        inputLabel: { ...newProps.slotProps.inputLabel, shrink: true },
      },
    };
    if (!newProps.value) {
      counterProps.value = min || 0;
    }
    newProps = { ...newProps, ...counterProps };
  } else if (type === "percentage") {
    newProps = {
      ...newProps,
      value: ATROCITY_some(value) ? value : "",
      slotProps: {
        ...newProps.slotProps,
        htmlInput: {
          style: {
            "& WebkitOuterSpinButton, & WebkitInnerSpinButton": {
              display: "none",
              WebkitAppearance: "none",
            },
          },
        },
      },
    };
  } else if (type === "number") {
    newProps = {
      ...newProps,
      slotProps: {
        ...newProps.slotProps,
        htmlInput: { min, max, ...inputProps },
      },
    };
  } else if (type === "autocomplete") {
    const { style } = other;
    const fullStyle = { ...style, flexWrap: "nowrap" };
    newProps = { ...newProps, style: fullStyle };
  }

  // Props "lixo" que chegam via spread mas o TextField MUI v9 não aceita
  // (vazariam pro DOM como atributos custom). Filtramos antes do spread.
  const STRIP_PROPS = new Set([
    "view",
    "hideActions",
    "renderTags",
    "noKeyboard",
    "customMask",
    "customFormat",
    "openTo",
    "views",
    "minDate",
    "maxDate",
    "disableFuture",
    "disablePast",
    "shrink",
    "form",
  ]);
  const cleanOther = Object.fromEntries(
    Object.entries(other).filter(([k]) => !STRIP_PROPS.has(k)),
  );
  const textFieldProps = { ...cleanOther, ...newProps };

  if (type === "select" && textFieldProps.options) {
    return renderSelect({ ...textFieldProps, mainOption, message, label }, classes);
  }
  if (type === "autocomplete") {
    // CRÍTICO: `value`, `onChange` e tipo são destructurados de `props` no
    // topo da função e NÃO estão em `cleanOther` (que vem de `other`/spread).
    // Sem eles aqui, o Autocomplete recebe `value=undefined` → form chega
    // null → seleção não persiste visualmente.
    return (
      <Autocomplete
        {...cleanOther}
        value={value}
        onChange={onChange}
        InputProps={InputProps}
        InputLabelProps={InputLabelProps}
        label={label}
        valueSize={valueSize}
      />
    );
  }
  if (type === "multiselect") {
    return (
      <Autocomplete
        mainOption={mainOption}
        {...cleanOther}
        value={value}
        onChange={onChange}
        InputProps={InputProps}
        InputLabelProps={InputLabelProps}
        valueSize={valueSize}
        label={label}
        multiselect
      />
    );
  }

  let t = "text";
  if (type === "number" || type === "password") {
    t = type;
  } else if (type === "percentage" || type === "counter") {
    t = "number";
  }

  // Injeta o ícone (i) de ajuda como endAdornment do TextField — assim ele
  // fica DENTRO do input field (coberto por overlays como calendar popper)
  // e não no label flutuante (que sobrepunha popovers/dropdowns).
  const finalInputProps = withHelpAdornment(
    textFieldProps.InputProps || textFieldProps.slotProps?.input,
    props,
  );

  // MUI v9 removeu `InputProps` do TextField — vai via `slotProps.input`.
  // Sem isso a prop vazava pro <div> root e o React reclamava
  // "React does not recognize the `InputProps` prop on a DOM element".
  const { InputProps: _droppedIP, ...textFieldRest } = textFieldProps;
  const finalSlotProps = {
    ...(textFieldRest.slotProps || {}),
    input: {
      ...(textFieldRest.slotProps?.input || {}),
      ...finalInputProps,
    },
  };

  return (
    <TextField
      label={label}
      {...textFieldRest}
      slotProps={finalSlotProps}
      autoComplete="just-ignore-it"
      type={t}
    />
  );
}

function renderInputComponent(props) {
  const { type = "text" } = props;

  if (isTextFieldType[type]) {
    return renderTextFieldInput(props);
  }

  if (isControlGroupType[type]) {
    const value = props.value ? props.value : Set();
    return renderControlGroup({ ...props, value });
  }

  if (isDateType[type]) {
    return renderDateFieldInput(props);
  }

  if (type === "toggle-switch") {
    const { classes, options, ...other } = props;
    const onYesElement = options?.find(
      (item) => item.get("value") === 1 || item.get("value") === true,
    );
    const onNoElement = options?.find(
      (item) => item.get("value") === 0 || item.get("value") === false,
    );
    return (
      <ToggleSwitch
        yesText={onYesElement && onYesElement.get("label")}
        noText={onNoElement && onNoElement.get("label")}
        {...other}
      />
    );
  }

  if (type === "file") {
    const { classes, ...other } = props;
    return <FileChooser {...other} />;
  }

  if (type === "filelist") {
    const { classes, ...other } = props;
    return <FileList {...other} />;
  }

  return <h3 style={{ color: "red" }}>{`${type} não é um tipo válido de input`}</h3>;
}

export default renderInputComponent;
